import React, { useState, useEffect } from 'react';
import Image from '@/components/atoms/Image';
import { ShareIcon, StarIcon } from '@heroicons/react/24/outline';
import { ProductTileProps, ProductTileVariant } from '@/components/molecules/product-tile/types';
import { Product } from '@/types/entity/product';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import useFormat from '@/hooks/useFormat';
import useDiscount from '@/hooks/useDiscount';
import DiscountTag from '@/components/molecules/product-tile/components/discount-tag';
import QuantityWidget from '@/components/atoms/quantity-widget';
import Typography from '@/components/atoms/typography';
import Button from '@/components/atoms/button';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { sdk } from '@/sdk/CommercetoolsSDK';
import { exclusivityMapping } from '@/constants/exclusivityMapping';
import { getLifecycleBadge, getIntroductoryPriceBadge } from '@/utils/product-lifecycle';

const ProductListTile = (props: ProductTileProps) => {
  const { locale } = useParams();
  const { item, onAddToCart, onAddToProject, parent } = props;
  const {
    name,
    description,
    inStock,
    sku,
    models,
    price,
    discountedPrice,
    currency,
    image,
    maxQuantity,
    restockableInDays,
    url,
    category,
    exclusiveProduct,
    productType,
    isNew,
    isBuilderRange,
    lifecycle,
  } = item;
  const lifecycleBadge = React.useMemo(() => getLifecycleBadge(lifecycle), [lifecycle]);
  const introPriceBadge = React.useMemo(
    () => getIntroductoryPriceBadge(models) || getIntroductoryPriceBadge(sku),
    [models, sku],
  );

  const { translate } = useTranslation();
  const { formatCurrency } = useFormat();
  const { isDiscounted, discountPercentage } = useDiscount(price, discountedPrice);
  const [target, setTarget] = React.useState('_blank');
  const { selectedBusinessUnit } = useStoreAndBusinessUnits();

  const [wishlistedSkus, setWishlistedSkus] = useState<string[]>([]);
  const [wishlistLoading, setWishlistLoading] = useState(true);
  useEffect(() => {
    const storeKey = locale === 'au' ? 'plumbershub-au' : 'plumbershub-nz';
    const fetchWishlist = async () => {
      try {
        const response = await sdk.composableCommerce.custom.getCustomerWishlist({
          pageNumber: 1,
          pageSize: 100,
          storeKey,
        });

        if (!response.isError && response.data?.data?.items) {
          const skus = response.data.data.items.map((item: any) => String(item.variantsku).trim());

          setWishlistedSkus(skus);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setWishlistLoading(false);
      }
    };

    fetchWishlist();
  }, []);
  const variantSku = String(item?.variants?.[0]?.sku ?? '').trim();
  const initialIsWishlisted = wishlistedSkus?.includes(variantSku) ?? false;

  const [isWishlisted, setIsWishlisted] = useState(initialIsWishlisted);

  useEffect(() => {
    setIsWishlisted(initialIsWishlisted);
  }, [initialIsWishlisted]);
  const handleAddToWishlist = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation
    e.stopPropagation();
    try {
      const storeKey = locale === 'au' ? 'plumbershub-au' : 'plumbershub-nz';
      const sku = item?.variants?.[0]?.sku;
      if (isWishlisted) {
        const response = await sdk.composableCommerce.custom.removeProductFromWishlist({ variantSku: sku, storeKey });
        if (!response.isError) {
          setIsWishlisted(false);
        }
        return;
      }
      const response = await sdk.composableCommerce.custom.addProductToWishlist(
        JSON.stringify({ variantSku: sku, storeKey }),
      );
      if (!response.isError) {
        setIsWishlisted(true);
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
    }
  };
  const isFormaRange = React.useMemo(() => {
    const checkRange = (val: string | any) => {
      if (typeof val === 'string') {
        const v = val.toLowerCase();
        return v === 'forma' || v === 'forma ii';
      }
      if (val && typeof val === 'object' && val['en-AU']) {
        const v = val['en-AU'].toLowerCase();
        return v === 'forma' || v === 'forma ii';
      }
      return false;
    };
    const prod = item as any;
    return (
      checkRange(prod?.attributesAlgolia?.['range']) ||
      checkRange(prod?.attributes?.['range']) ||
      checkRange(prod?.variants?.[0]?.attributes?.['range'])
    );
  }, [item]);

  const isPLUMWO = React.useMemo(() => {
    const prod = item as any;
    const extractAttr = (val: any): any => {
      if (!val) return val;
      if (Array.isArray(val)) return val[0];
      if (typeof val === 'object' && val !== null) return val.key || val.label || val;
      return val;
    };
    const exclusiveProductNZ =
      extractAttr(prod?.attributesAlgolia?.['exclusive-product-NZ']) ||
      extractAttr(prod?.attributes?.['exclusive-product-NZ']) ||
      extractAttr(prod?.variants?.[0]?.attributes?.['exclusive-product-NZ']);
    return exclusiveProductNZ === 'PLUMWO';
  }, [item]);
  const exclusiveProductList = React.useMemo(() => {
    const prod = item as any;

    const toArray = (val: any): string[] => {
      if (!val) return [];
      if (Array.isArray(val)) return val;

      if (typeof val === 'object') {
        const extracted = val.key || val.label || val;
        return extracted ? [extracted] : [];
      }

      return [val];
    };
    let attributeKey = '';
    if (locale === 'nz') {
      attributeKey = 'exclusive-product-NZ';
    } else if (locale === 'au') {
      attributeKey = 'exclusive-product-AU';
    }

    if (!attributeKey) return [];

    const sources = [
      prod?.attributesAlgolia?.[attributeKey],
      prod?.attributes?.[attributeKey],
      prod?.variants?.[0]?.attributes?.[attributeKey],
    ];

    for (const src of sources) {
      const arr = toArray(src);
      if (arr.length > 0) return arr;
    }

    return [];
  }, [item, locale]);
  return (
    <Link href={url ?? '#'} className="">
      <div className={`grid grid-cols-1 gap-2 p-2 lg:grid-cols-2`}>
        <div className="grid grid-cols-[62px_1fr] items-center gap-2 lg:grid-cols-[80px_1fr]">
          <div className="relative h-[62px] w-[62px] lg:h-[80px] lg:w-[80px]">
            {exclusiveProductList
              ?.filter((p) => p && String(p).trim().toLowerCase() !== 'null')
              .map((p) => {
                const partnerName = exclusivityMapping[p?.toUpperCase()] || String(p).toUpperCase();
                return (
                  <div className="absolute bottom-0 z-10 flex w-full items-center justify-center !bg-[#A6C9CC] py-1 !text-[6px] !text-[#000000]">
                    EXCLUSIVE TO {partnerName}
                  </div>
                );
              })}
            {/* {isNew && !lifecycleBadge && (
              <div className={`absolute left-1 ${isFormaRange || isPLUMWO ? 'top-4' : 'top-1'} px-1 py-0.5 rounded-[5px] text-white bg-[#FA2727] z-10 flex justify-center items-center text-[6px] font-semibold uppercase`}>
                NEW
              </div>
            )} */}
            {(lifecycleBadge || introPriceBadge) && (
              <div
                className={`absolute left-1 ${isFormaRange || isPLUMWO ? 'top-1' : 'top-1'} z-10 flex w-max flex-nowrap items-center gap-0.5 whitespace-nowrap`}
              >
                {lifecycleBadge && (
                  <div
                    className="flex items-center justify-center rounded-[5px] px-1 py-0.5 text-[6px] font-semibold uppercase text-white"
                    style={{ backgroundColor: lifecycleBadge.color }}
                  >
                    {lifecycleBadge.label}
                  </div>
                )}
                {introPriceBadge && (
                  <div
                    className="flex items-center justify-center rounded-[5px] px-1 py-0.5 text-[6px] font-semibold uppercase text-white"
                    style={{ backgroundColor: introPriceBadge.color }}
                  >
                    {introPriceBadge.label}
                  </div>
                )}
              </div>
            )}
            {image ? (
              <Image fill src={Array.isArray(image) ? image[0] : image} alt={name} />
            ) : (
              <Image src="/images/No_product_Image.jpg" className="h-full w-full" alt="empty product image" />
            )}
          </div>
          <div className={introPriceBadge ? 'translate-y-4 lg:translate-y-0' : ''}>
            <div className="text-[14px] font-normal leading-[20px] text-[#090909]">{category}</div>
            <div className="text-[16px] font-medium leading-[24px] text-[#090909]">{name}</div>
          </div>
        </div>
        <div className="inline-grid items-center text-16 font-medium leading-6 text-[#090909]">
          <div
            className={`grid items-center gap-1 ${
              !isBuilderRange ? 'lg:grid-cols-4' : 'lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4'
            }`}
          >
            <div className="order-1 break-words lg:order-none">
              {models && (
                <div className="flex items-center">
                  <div className="mr-1 text-12 font-normal leading-4 text-[#666666] lg:hidden">
                    Code
                    {':'}
                  </div>{' '}
                  <p className="overflow-hidden text-clip text-[16px] font-medium leading-[24px] text-[#090909]">
                    {models}
                  </p>
                </div>
              )}
            </div>
            <div
              className={`order-2 hidden lg:order-none lg:flex lg:pl-1 ${
                isBuilderRange || lifecycle?.toUpperCase()?.trim() === 'PROTOTYPE' ? 'visible' : 'visible'
              }`}
            >
              {!isBuilderRange && lifecycle?.toUpperCase()?.trim() !== 'PROTOTYPE' ? (
                <>
                  <div className="md:hidden">{translate('product.rrp.price')}</div>
                  <div className="text-[16px] font-medium leading-[24px] text-[#090909]">
                    {formatCurrency(price, currency)}
                  </div>
                </>
              ) : (
                <Button
                  variant="ghost"
                  type="submit"
                  className="flex !px-2 !max-w-[300px] cursor-pointer items-center gap-2 !rounded-none !px-0 !text-12 font-medium bg-[#D9EFF1]"
                >
                  <svg
                    stroke="currentColor"
                    fill="currentColor"
                    strokeWidth="0"
                    viewBox="0 0 512 512"
                    className="text-[20px] md:text-[30px] 3xl:text-[20px]"
                    height="1em"
                    width="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 110c23.196 0 42 18.804 42 42s-18.804 42-42 42-42-18.804-42-42 18.804-42 42-42zm56 254c0 6.627-5.373 12-12 12h-88c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h12v-64h-12c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h64c6.627 0 12 5.373 12 12v100h12c6.627 0 12 5.373 12 12v24z"></path>
                  </svg>{' '}
                  <span className="text-left !text-[13px] md:pt-0 px-1">
                    
                    <Link href="/contact-us/">
                      <span className=" underline">Contact Us</span>{' '}
                    </Link>
                    about this product
                  </span>
                </Button>
              )}
            </div>

            {!isBuilderRange && lifecycle?.toUpperCase()?.trim() !== 'PROTOTYPE' && (
              <div className="order-2 lg:hidden">
                <div className="flex items-center">
                  <div className="mr-1 text-[16px] font-medium leading-[24px] text-[#090909]">RRP :</div>
                  <div>{formatCurrency(price, currency)} <span className="text-[14px]">(inc.GST)</span></div>
                </div>
              </div>
            )}
            {/* Desktop */}
            <div
              className={`${parent === 'project-builder' ? 'md:justify-end' : ''} hidden items-center gap-2 lg:flex`}
            >
              
                <Button
                  variant="ghost"
                  type="submit"
                  className={`${
                    parent === 'project-builder' ? 'w-full !px-1 py-[14px]' : '!px-4 py-[10px]'
                  } cursor-pointer rounded !bg-[#A6C9CC] text-16 !font-semibold text-black`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAddToProject && onAddToProject();
                  }}
                >
                  Add to Project
                </Button>
              
            </div>

            <div
              className="hidden w-full cursor-pointer items-center justify-center lg:flex"
              onClick={handleAddToWishlist}
            >
              <button className="rounded-full transition-colors" aria-label="Add to wishlist">
                {isWishlisted ? (
                  <StarIconSolid className="h-6 w-6 cursor-pointer text-gray-800" />
                ) : (
                  <StarIcon className="h-6 w-6 cursor-pointer text-gray-600" />
                )}
              </button>
            </div>

            {/* Mobile */}
            <div className="order-3 flex flex-col gap-2 lg:hidden">
              <div className="flex cursor-pointer items-center" onClick={handleAddToWishlist}>
                <div className="mr-1 pt-2 text-12 font-normal leading-4 text-[#666666]">Add to Saved Products:</div>

                <button className="translate-y-1 rounded-full transition-colors" aria-label="Add to wishlist">
                  {isWishlisted ? (
                    <StarIconSolid className="h-6 w-6 cursor-pointer text-gray-800" />
                  ) : (
                    <StarIcon className="h-6 w-6 cursor-pointer text-gray-600" />
                  )}
                </button>
              </div>
              {isBuilderRange && (
                <Button
                  variant="ghost"
                  type="submit"
                  className="flex  cursor-pointer items-center gap-2 !rounded-none !px-0 !text-12 font-medium bg-[#D9EFF1]"
                >
                  <svg
                    stroke="currentColor"
                    fill="currentColor"
                    strokeWidth="0"
                    viewBox="0 0 512 512"
                    className="text-[20px] md:text-[30px] 3xl:text-[20px]"
                    height="1em"
                    width="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 110c23.196 0 42 18.804 42 42s-18.804 42-42 42-42-18.804-42-42 18.804-42 42-42zm56 254c0 6.627-5.373 12-12 12h-88c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h12v-64h-12c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h64c6.627 0 12 5.373 12 12v100h12c6.627 0 12 5.373 12 12v24z"></path>
                  </svg>{' '}
                  <span className="text-left !text-[13px] md:pt-0 px-1">
                    
                    <Link href="/contact-us/">
                      <span className=" underline">Contact Us</span>{' '}
                      
                    </Link>
                    about this product
                  </span>
                </Button>

              )}
                <Button
                  variant="ghost"
                  type="submit"
                  className="w-full cursor-pointer rounded !bg-[#A6C9CC] py-[14px] text-16 !font-semibold text-black"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAddToProject && onAddToProject();
                  }}
                >
                  Add to Project
                </Button>
              
            </div>

            <div>
              {/* <div className="flex items-center cursor-pointer" onClick={handleAddToWishlist}>
                <div className="mr-1 mt-2 text-12 font-normal leading-4 text-[#666666] lg:hidden cursor-pointer" >
                  Saved Products
                  {':'}
                </div>{' '}
                <button
                  className=" translate-y-1 rounded-full  transition-colors"
                  
                  aria-label="Add to wishlist"
                >
                  {isWishlisted
                    ? <StarIconSolid className="h-6 w-6 text-gray-800 cursor-pointer" />
                    : <StarIcon className="h-6 w-6 text-gray-600 cursor-pointer" />
                  }
                </button>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductListTile;
